
import { mapActions } from 'vuex'
import logoutMixin from '@/mixins/logoutMixin'
import categoryMixin from '@/mixins/categoryMixin'

export default {
  name: 'ProductsSidebar',
  mixins: [logoutMixin, categoryMixin],
  data() {
    return {
      showAllDomains: false,
      isLoadingDomains: true,
      selectedDomain: null,
      hoveredCategory: null,
      isClosing: false,
      flyoutTimer: null,
      hoverEnterTimer: null,
      quickLinks: [],
      widgetLoaded: false,
      reviewStats: { rating: null, count: null },
      socialNetworks: [
        {
          key: 'facebook',
          href: 'https://www.facebook.com/agmvision/',
          icon: 'facebook-f',
        },
        {
          key: 'instagram',
          href: 'https://www.instagram.com/agm_vision/',
          icon: 'instagram',
        },
        {
          key: 'tiktok',
          href: 'https://www.tiktok.com/@agmvision',
          icon: 'tiktok',
        },
        {
          key: 'youtube',
          href: 'https://www.youtube.com/@agmvision',
          icon: 'youtube',
        },
      ],
      helpLinks: [
        {
          name: 'compte',
          label: 'components.productsSidebar.myAccount',
        },
        {
          name: 'retour-remboursement',
          label: 'components.productsSidebar.returnAndRefund',
        },
        {
          name: 'contact',
          label: 'components.productsSidebar.contactUs',
        },
      ],
    }
  },
  computed: {
    domains() {
      return this.$store.state.domains || []
    },
    visibleDomains() {
      if (!this.$device.isMobile) return this.domains
      return this.showAllDomains ? this.domains : this.domains.slice(0, 10)
    },
    isAdmin() {
      return (
        this.$auth.loggedIn && this.$auth.user.role?.identifier === 'admin'
      )
    },
    selectedDomainCategories() {
      if (!this.selectedDomain) return []
      return this.mergeChildrenAndLinks(this.selectedDomain)
    },
    hoveredCategoryItems() {
      if (!this.hoveredCategory) return []
      return this.mergeChildrenAndLinks(this.hoveredCategory)
    },
    formattedRating() {
      const rating = parseFloat(this.reviewStats.rating)
      return isNaN(rating) ? '4.9' : rating.toFixed(1)
    },
    formattedReviewsCount() {
      const count = Number(this.reviewStats.count) || 0
      return this.$tc('components.menu.reviews', count, { count })
    },
  },
  beforeDestroy() {
    clearTimeout(this.flyoutTimer)
    clearTimeout(this.hoverEnterTimer)
  },
  methods: {
    ...mapActions(['setDomains', 'setDomainSubmenu']),
    mergeChildrenAndLinks(category) {
      const items = [
        ...(category.children || []),
        ...(category.links || [])
          .filter(item => item.category_b)
          .map(item => ({
            ...item.category_b,
            is_link: true,
            position: item.position,
          })),
      ]
      return items
        .filter(item => this.isAdmin || item.is_active !== false)
        .sort((a, b) => a.position - b.position)
    },
    async onShown() {
      this.isLoadingDomains = true
      await this.setDomains()
      this.isLoadingDomains = false
      this.loadQuickLinks()
      await this.loadReviewStats()
      if (this.$device.isMobile) {
        await this.$nextTick()
        this.loadSkeepersWidget()
      }
    },
    async loadReviewStats() {
      try {
        const response =
          await this.$verifiedReviewsService.fetchSummarySkeepers()
        this.reviewStats = {
          rating: response.average || 4.9,
          count: response.count_total_since_creation || null,
        }
      } catch {
        this.reviewStats = { rating: 4.9, count: null }
      }
    },
    async loadQuickLinks() {
      try {
        const { data } = await this.$quickLinkService.fetch({
          sort: 'position',
        })
        this.quickLinks = data?.length
          ? data.map(ql => ({
              ...ql,
              link: this.localePath(ql.localized_link || ql.link),
            }))
          : []
      } catch {
        this.quickLinks = []
      }
    },
    loadSkeepersWidget() {
      if (!process.client) return
      const widgetId = 'db60a874-e6ec-44e5-b7d7-79c0bd78a76a'
      const scriptId = 'skeepers-sidebar-widget'
      const widgetContainer = document.getElementById(widgetId)

      if (widgetContainer) {
        if (widgetContainer.children.length > 0) {
          this.widgetLoaded = true
          return
        }

        const observer = new MutationObserver(() => {
          if (widgetContainer.children.length > 0) {
            this.widgetLoaded = true
            observer.disconnect()
          }
        })
        observer.observe(widgetContainer, { childList: true })
      }

      const existingScript = document.getElementById(scriptId)
      if (existingScript) {
        existingScript.remove()
      }

      const script = document.createElement('script')
      script.id = scriptId
      script.src = `https://widgets.rr.skeepers.io/generated/5e0737dc-e1e6-bba4-f965-f42331423733/${widgetId}.js`
      script.defer = true
      document.body.appendChild(script)
    },
    onHidden() {
      this.isClosing = false
      this.selectedDomain = null
      this.hoveredCategory = null
      clearTimeout(this.flyoutTimer)
      clearTimeout(this.hoverEnterTimer)
    },
    closeSidebar() {
      this.isClosing = true
      this.hoveredCategory = null
      clearTimeout(this.flyoutTimer)
      clearTimeout(this.hoverEnterTimer)
      this.$refs.sidebar?.hide()
    },
    async handleLogout() {
      await this.logout()
      this.closeSidebar()
    },
    async selectDomain(domain) {
      this.hoveredCategory = null
      clearTimeout(this.flyoutTimer)
      this.selectedDomain = domain
      await this.setDomainSubmenu(domain.id)
      const submenu = this.$store.state.domainSubmenus[domain.id]
      if (submenu && this.selectedDomain && this.selectedDomain.id === domain.id) {
        this.selectedDomain = submenu
      }
    },
    goBackToMainMenu() {
      this.selectedDomain = null
      this.hoveredCategory = null
      clearTimeout(this.flyoutTimer)
    },
    onCategoryMouseEnter(category) {
      if (this.$device.isMobile || this.isClosing) return
      clearTimeout(this.flyoutTimer)
      clearTimeout(this.hoverEnterTimer)
      const hasItems =
        (category.children && category.children.length) ||
        (category.links && category.links.length)
      if (hasItems) {
        this.hoverEnterTimer = setTimeout(() => {
          this.hoveredCategory = category
        }, 200)
      } else {
        this.hoveredCategory = null
      }
    },
    onCategoryMouseLeave() {
      if (this.$device.isMobile) return
      clearTimeout(this.hoverEnterTimer)
      this.flyoutTimer = setTimeout(() => {
        this.hoveredCategory = null
      }, 150)
    },
    onFlyoutMouseEnter() {
      if (this.isClosing) return
      clearTimeout(this.flyoutTimer)
      clearTimeout(this.hoverEnterTimer)
    },
    onFlyoutMouseLeave() {
      this.flyoutTimer = setTimeout(() => {
        this.hoveredCategory = null
      }, 150)
    },
    getFlyoutSubLink(sub) {
      if (sub.is_link) {
        return this.getUrlFromNestedParents(sub)
      }
      if (this.hoveredCategory.is_link) {
        const level = this.hoveredCategory.count_parents_recursive
        if (level == 0) {
          return this.getCategoryLink(this.hoveredCategory, sub)
        }
        if (level == 1 && this.hoveredCategory.nested_parents) {
          return this.getSubcategoryLink(this.hoveredCategory.nested_parents, this.hoveredCategory, sub)
        }
      }
      return this.getSubcategoryLink(this.selectedDomain, this.hoveredCategory, sub)
    },
    getSubcategoryLink(domain, category, subcategory) {
      return this.localePath({
        name: 'categories-domain-category-subcategory',
        params: {
          domain: domain.slug,
          category: category.slug,
          subcategory: subcategory.slug,
        },
      })
    },
    getDomainLink(domain) {
      return this.localePath({
        name: 'categories-domain',
        params: { domain: domain.slug },
      })
    },
    getCategoryLink(domain, category) {
      return this.localePath({
        name: 'categories-domain-category',
        params: { domain: domain.slug, category: category.slug },
      })
    },
  },
}
